import React, { useEffect } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { vehicle as rules } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  FormControl,
  Grid,
  ListSubheader,
  MenuItem,
  Select,
  Typography,
  TextField,
  IconButton,
  Box,
  styled,
} from '@mui/material';
import { ActionCategoryLabels } from 'further-ui/labels';
import { DeleteOutline } from '@mui/icons-material';
import Spacing from 'components/Spacing';
import ButtonGroup from 'components/ButtonGroup';
import Button from 'components/Button';
import Table from 'components/Table';
import Heading from 'components/Heading';
import { z } from 'zod';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  ActionTransactionSubTypes,
  ActionCategory,
  ActionRef,
  ActionTypes as ActionTypesEnum,
} from 'further-types/lp/action';
import { AccountMapping } from 'further-types/lp/account';
import { ApiResponses } from 'lib/trpcClient';
import { groupBy, sortBy } from 'lodash';
import { getActionLabel } from './processLabels';

type ActionTypesFromApi = ApiResponses['action']['listTypes'];

type FormValues = z.infer<typeof rules.UpdateAccountMappings>;

type AccountMappingWithOriginalIndex = AccountMapping & {
  id: string;
  originalIndex: number;
  isCustom?: boolean;
};

type Props = {
  vehicleId: string;
  transactionTypes: Array<{
    label: string;
    value: string | null;
  }>;
  actionTypes: ActionTypesFromApi;
  defaultAccountMappings: Array<AccountMapping>;
};

const StyledAddButtonWrapper = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: '16px',
}));

const categoryOrder: Array<ActionCategory> = [
  ActionCategory.Income,
  ActionCategory.Expense,
  ActionCategory.Investment,
  ActionCategory.Realisation,
  ActionCategory.ManagementFees,
  ActionCategory.CapitalActions,
  ActionCategory.FX,
];

const AccountMappingGrid: React.FC<Props> = ({
  vehicleId,
  defaultAccountMappings,
  transactionTypes,
}) => {
  const utils = api.useUtils();

  const { control, handleSubmit, getValues, reset } = useForm<FormValues>({
    resolver: zodResolver(rules.UpdateAccountMappings),
    defaultValues: {
      accountMappings: defaultAccountMappings,
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'accountMappings',
  });

  // Reset the form when defaultAccountMappings changes (after refetch)
  useEffect(() => {
    reset({
      accountMappings: defaultAccountMappings,
    });
  }, [defaultAccountMappings, reset]);

  const editMappings = api.vehicle.updateAccountMappings.useMutation(
    useHandleTrpcMutation()({
      successMessage: 'Account mappings updated.',
      onSuccess: async () => {
        // Force refetch the vehicle query to get updated account mappings with new custom action type IDs
        await utils.vehicle.getById.refetch({ vehicleId });
        await utils.action.listTypes.refetch({ vehicleId });
      },
      onError: async () => {
        // Refetch on error to ensure data is in sync
        await utils.vehicle.getById.refetch({ vehicleId });
        await utils.action.listTypes.refetch({ vehicleId });
      },
    }),
  );

  const onSubmit = (data: FormValues) => {
    editMappings.mutate({
      vehicleId,
      accountMappings: data.accountMappings,
    });
  };

  const getActionRefForCategory = (category: ActionCategory): ActionRef => {
    switch (category) {
      case ActionCategory.Income:
      case ActionCategory.Expense:
        return ActionRef.IncomeOrExpense;
      case ActionCategory.Investment:
        return ActionRef.Investment;
      case ActionCategory.Realisation:
        return ActionRef.Realisation;
      case ActionCategory.ManagementFees:
        return ActionRef.Fees;
      case ActionCategory.CapitalActions:
        return ActionRef.CapitalAction;
      case ActionCategory.FX:
        return ActionRef.BankAccountTransfer;
      default:
        return ActionRef.IncomeOrExpense;
    }
  };

  const isCustomMapping = (mapping: AccountMapping) => {
    return mapping.actionType === ActionTypesEnum.CustomAction;
  };

  const addCustomMapping = (category: ActionCategory) => {
    const newMapping = {
      actionRef: getActionRefForCategory(category),
      actionType: ActionTypesEnum.CustomAction,
      actionCategory: category,
      customActionType: '',
      customActionTypeId: null,
      transactionSubType: ActionTransactionSubTypes.CreditDebitPair,
      debitTransactionTypeId: '',
      creditTransactionTypeId: '',
    };
    append(newMapping);
  };

  const removeCustomMapping = (index: number) => {
    remove(index);
  };

  // Group mappings by category while preserving original indices
  const mappingsWithIndices = fields.map((field, index) => ({
    ...field,
    customActionTypeId: field.customActionTypeId,
    originalIndex: index,
    isCustom: isCustomMapping(field),
  }));

  const grouped = groupBy(mappingsWithIndices, (m) => m.actionCategory);

  // Sort by category order, with custom mappings always at the end
  const groupedMappings = categoryOrder
    .filter((category) => grouped[category]?.length > 0)
    .map((category) => {
      const categoryMappings = grouped[category];
      const regularMappings = categoryMappings.filter((m) => !m.isCustom);
      const customMappings = categoryMappings.filter((m) => m.isCustom);

      return {
        category,
        mappings: [
          ...sortBy(regularMappings, ['actionType', 'transactionSubType']),
          ...customMappings,
        ],
      };
    });

  const createColumns = () => [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.actionRef`}
          control={control}
          defaultValue={row.actionRef}
          render={() => (
            <FormControl fullWidth>
              {row.isCustom ? (
                <Controller
                  name={`accountMappings.${row.originalIndex}.customActionType`}
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Enter custom action name"
                      size="small"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  component="span"
                >
                  {getActionLabel(row)}
                </Typography>
              )}
            </FormControl>
          )}
        />
      ),
    },
    {
      key: 'debitTransactionTypeId',
      label: 'Debit',
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.debitTransactionTypeId`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <Select
                {...field}
                fullWidth
                disabled={
                  getValues(
                    `accountMappings.${row.originalIndex}.debitTransactionTypeId`,
                  ) === null
                }
              >
                {transactionTypes.map((option) =>
                  !option.value ? (
                    <ListSubheader key={option.label}>
                      {option.label}
                    </ListSubheader>
                  ) : (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        />
      ),
    },
    {
      key: 'creditTransactionTypeId',
      label: 'Credit',
      render: (row: AccountMappingWithOriginalIndex) => (
        <Controller
          name={`accountMappings.${row.originalIndex}.creditTransactionTypeId`}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <FormControl fullWidth error={!!error}>
              <Select
                {...field}
                fullWidth
                disabled={
                  getValues(
                    `accountMappings.${row.originalIndex}.creditTransactionTypeId`,
                  ) === null
                }
              >
                {transactionTypes.map((option) =>
                  !option.value ? (
                    <ListSubheader key={option.label}>
                      {option.label}
                    </ListSubheader>
                  ) : (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ),
                )}
              </Select>
            </FormControl>
          )}
        />
      ),
    },
    {
      key: 'actions',
      label: '',
      render: (row: AccountMappingWithOriginalIndex) =>
        row.isCustom ? (
          <IconButton
            onClick={() => removeCustomMapping(row.originalIndex)}
            size="small"
            color="error"
            disabled={row.isInUse}
            title={
              row.isInUse
                ? 'Cannot delete: This mapping is currently in use'
                : 'Delete mapping'
            }
          >
            <DeleteOutline />
          </IconButton>
        ) : null,
    },
  ];

  const columns = createColumns();

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Spacing size="md">
        <Grid container spacing={4}>
          <Spacing size="lg">
            {groupedMappings.map(({ category, mappings }) => (
              <Grid size={12} key={category}>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <Heading variant="h4">
                      {ActionCategoryLabels[category]}
                    </Heading>
                  </Grid>
                  <Grid size={12}>
                    <Table
                      columns={columns}
                      tablebody={mappings}
                      fixedColumns
                      getRowId={(row: AccountMappingWithOriginalIndex) =>
                        row.id
                      }
                    />
                    {(category === ActionCategory.Income ||
                      category === ActionCategory.Expense) && (
                      <StyledAddButtonWrapper>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => addCustomMapping(category)}
                          size="large"
                        >
                          {`Add new`}
                        </Button>
                      </StyledAddButtonWrapper>
                    )}
                  </Grid>
                </Grid>
              </Grid>
            ))}
          </Spacing>
        </Grid>
        <ButtonGroup>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            loading={editMappings.isPending}
          >
            Save changes
          </Button>
        </ButtonGroup>
      </Spacing>
    </form>
  );
};

export default AccountMappingGrid;
